import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RuleSet } from 'styled-components';
import { Photo } from '../../../graphql/types';
import {
  GalleryCarouselContainer,
  GalleryCarouselImage,
  GalleryCarouselImageWrapper,
  Overlay,
  OverlayText
} from './styled';

const VISIBLE_PHOTOS_WITHOUT_OVERLAY = 7;

interface GalleryCarouselProps {
  photos?: Photo[];
  pageNumber: number;
  shouldOverlayRender?: boolean;
  carouselPhotoWidth?: number;
  carouselPhotoHeight?: number;
  photoLimit?: number;
  carouselStyles?: RuleSet<object>;
  onOpenGallery?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    photoNumber?: number
  ) => void;
  scrollToPhoto: (index: number) => void;
  hideFromWidth?: string;
  description?: string;
}

export const GalleryCarousel: React.FC<GalleryCarouselProps> = ({
  photos,
  pageNumber,
  shouldOverlayRender = true,
  carouselPhotoWidth = 75,
  carouselPhotoHeight = 50,
  photoLimit = 0,
  carouselStyles,
  onOpenGallery,
  scrollToPhoto,
  hideFromWidth,
  description = ''
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  if (!photos?.length) return null;

  const limitedPhotos = photoLimit ? photos.slice(0, photoLimit) : photos;
  const hiddenCount = photos.length - photoLimit;

  const handleMouseEnter = (index: number) => {
    scrollToPhoto(index + 1);

    if (containerRef.current) {
      const container = containerRef.current;
      const child = container.children[index];

      if (child) {
        const containerWidth = container.clientWidth;
        const childOffsetLeft = child.offsetLeft;
        const childWidth = child.clientWidth;

        const scrollTo = childOffsetLeft - containerWidth / 2 + childWidth / 2;

        container.scrollTo({
          left: scrollTo,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <GalleryCarouselContainer
      ref={containerRef}
      $photosCount={photos.length}
      $limit={photoLimit}
      $styles={carouselStyles}
      $carouselPhotoWidth={carouselPhotoWidth}
      $hideFromWidth={hideFromWidth}
    >
      {limitedPhotos.map((photo, index) => {
        const isOverlayVisible =
          index === VISIBLE_PHOTOS_WITHOUT_OVERLAY && hiddenCount > 0;
        const photoToTake = photo.thumbnail || photo.f660x496;

        return (
          <GalleryCarouselImageWrapper key={photoToTake}>
            <GalleryCarouselImage
              src={photoToTake}
              alt={`${description ? `${description}, ` : ''}photo ${index + 1} from ${limitedPhotos.length}`}
              width={carouselPhotoWidth}
              height={carouselPhotoHeight}
              onClick={e => onOpenGallery?.(e, index + 1)}
              onMouseEnter={() => handleMouseEnter(index)}
              $isActive={index === pageNumber - 1}
            />

            {isOverlayVisible && shouldOverlayRender && (
              <Overlay
                onMouseEnter={() => handleMouseEnter(index)}
                onClick={e => onOpenGallery?.(e, index + 1)}
              >
                <OverlayText tag="caption_1">
                  {t('pages.current_item.hidden_photo_count', {
                    count: hiddenCount
                  })}
                </OverlayText>
              </Overlay>
            )}
          </GalleryCarouselImageWrapper>
        );
      })}
    </GalleryCarouselContainer>
  );
};
